import { MedplumClient } from "@medplum/core";
import { Coding, Organization, Reference } from "@medplum/fhirtypes";

import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";

import {
  DEVELOPMENT_FEATURE_FLAG_VALUES,
  DevelopmentFeatureFlags,
  PRACTICE_FEATURE_FLAG_VALUES,
  PracticeFeatureFlags,
} from "../constants/feature-flags";
import { FeatureFlag } from "../types/feature-flag";

import { featureTagKey } from "./feature-flag-tags";

const QUILTED_FEATURE_FLAG_SYSTEMS: ReadonlySet<string> = new Set([
  QuiltedHealthCodeSystems.PracticeFeatureFlags,
  QuiltedHealthCodeSystems.DevelopmentFeatureFlags,
]);

function isQuiltedFeatureFlagTag(tag: Coding): boolean {
  return Boolean(tag.system && QUILTED_FEATURE_FLAG_SYSTEMS.has(tag.system));
}

/**
 * Returns tags on the organization that are not Quilted practice/development feature-flag codings.
 */
export function getNonFeatureFlagMetaTags(organization: Organization): Coding[] {
  return organization.meta?.tag?.filter((tag) => !isQuiltedFeatureFlagTag(tag)) ?? [];
}

/**
 * Builds `Coding` entries for enabled practice and development feature flags (one per code, deduped by system+code).
 */
export function buildFeatureFlagCodings(enabled: {
  practice: readonly PracticeFeatureFlags[];
  development: readonly DevelopmentFeatureFlags[];
}): Coding[] {
  const out: Coding[] = [];
  const seen = new Set<string>();

  const push = (system: string, code: string) => {
    const coding: Coding = { system, code };
    const key = featureTagKey(coding);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(coding);
  };

  for (const code of new Set(enabled.practice))
    push(QuiltedHealthCodeSystems.PracticeFeatureFlags, code);
  for (const code of new Set(enabled.development))
    push(QuiltedHealthCodeSystems.DevelopmentFeatureFlags, code);

  return out;
}

/**
 * Returns a shallow copy of the organization whose `meta.tag` replaces all Quilted feature-flag
 * tags with codings for the given enabled sets, preserving every other tag unchanged.
 */
export function setOrganizationFeatureTags(
  organization: Organization,
  enabled: {
    practice: readonly PracticeFeatureFlags[];
    development: readonly DevelopmentFeatureFlags[];
  },
): Organization {
  const preservedTags = getNonFeatureFlagMetaTags(organization);
  const featureTags = buildFeatureFlagCodings(enabled);
  const nextTag = [...preservedTags, ...featureTags];

  return {
    ...organization,
    meta: {
      ...organization.meta,
      tag: nextTag,
    },
  };
}

/**
 * Splits an organization's feature-flag tags into practice/development arrays, keyed by
 * `tag.system` and validated against the *current* enum members for that system — not just
 * truthiness. A tag whose code was moved or removed from its system's enum (e.g. a flag that
 * migrated from PracticeFeatureFlags to DevelopmentFeatureFlags) is dropped rather than carried
 * through: `isFeatureEnabled`'s check is a plain string comparison against both arrays, so an
 * enum member that happens to share a string value with a differently-categorized legacy tag
 * would otherwise still read as enabled after the migration.
 */
export function getFeatures(organization: Organization) {
  const practiceFeatures =
    organization.meta?.tag
      ?.filter((tag) => tag.system === QuiltedHealthCodeSystems.PracticeFeatureFlags)
      ?.map((tag) => tag.code as PracticeFeatureFlags)
      ?.filter((code): code is PracticeFeatureFlags =>
        Boolean(code && PRACTICE_FEATURE_FLAG_VALUES.has(code)),
      ) ?? [];

  const developmentFeatures =
    organization.meta?.tag
      ?.filter((tag) => tag.system === QuiltedHealthCodeSystems.DevelopmentFeatureFlags)
      ?.map((tag) => tag.code as DevelopmentFeatureFlags)
      ?.filter((code): code is DevelopmentFeatureFlags =>
        Boolean(code && DEVELOPMENT_FEATURE_FLAG_VALUES.has(code)),
      ) ?? [];

  return { practice: practiceFeatures, development: developmentFeatures };
}

export function isFeatureEnabled(organization: Organization, feature: FeatureFlag) {
  const features = getFeatures(organization);
  return (
    features.practice.includes(feature as PracticeFeatureFlags) ||
    features.development.includes(feature as DevelopmentFeatureFlags)
  );
}

export async function isFeatureEnabledAsync(
  medplum: MedplumClient,
  organizationReference: Reference<Organization> | undefined,
  feature: FeatureFlag,
) {
  if (!organizationReference) return false;

  const organization = await medplum.readReference<Organization>(
    organizationReference as Reference<Organization>,
  );
  if (!organization) return false;

  return isFeatureEnabled(organization, feature);
}
